import { useAudioFromUrl } from '@/hooks/useAudioFromUrl';
import { useAzureSynthesizer } from '@/hooks/useAzureSynthesizer';
import { AudioError } from '@/state/presenters';
import { IVoice, Providers, VoiceConfig } from '@d-id/types';
import { useEffect } from 'react';
import { atom } from 'recoil';
import { useAflorithmic } from './useAflorithmic';
import { useGoogle } from './useGoogle';
import { useStreamPlayer } from './useStreamPlayer';

export enum PlayerMode {
    NotLoaded = 0,
    Loading,
    Ready,
    Playing,
}

export const playerModeAtom = atom({
    key: 'player-mode-atom',
    default: PlayerMode.NotLoaded,
});

export const activePlayerIdAtom = atom({
    key: 'active-player-id',
    default: '',
});

const emptyPlayer = {
    playerMode: PlayerMode.NotLoaded,
    play: () => {},
    pause: () => {},
    error: null,
};

interface Props {
    voice: IVoice | null;
    text: string;
    voiceConfig: VoiceConfig;
    language: string | null;
    playerId?: string;
    useVoicePreviewUrl?: boolean;
    ttsError?: AudioError | null;
    setTtsError?: (error: AudioError | null) => void;
}

export const useAudioPlayer = ({ voice, text, voiceConfig, language, playerId, useVoicePreviewUrl, ttsError, setTtsError }: Props) => {
    const azurePlayer = useAzureSynthesizer({
        voice,
        text,
        language,
        voiceConfig,
        playerId,
        ttsError,
        setTtsError,
    });
    const elevenLabsPlayer = useStreamPlayer({ voice, text, voiceConfig, playerId, ttsError, setTtsError });
    const aflorithmicPlayer = useAflorithmic({ voice, text, voiceConfig, playerId, ttsError });
    const googlePlayer = useGoogle({ voice, text, playerId, ttsError, setTtsError });
    const urlPlayer = useAudioFromUrl({ voice, playerId, ttsError, setTtsError });

    useEffect(() => {
        if (playerId === 'main-player') {
            aflorithmicPlayer.pause?.();
            elevenLabsPlayer.pause?.();
            azurePlayer.pause?.();
            googlePlayer.pause?.();
            urlPlayer.pause?.();
        }
    }, [voice, voiceConfig, text]);

    if (!voice) {
        return emptyPlayer;
    }

    if (voice.previewUrl && useVoicePreviewUrl) {
        return urlPlayer;
    }

    const providerToPlayer = {
        [Providers.Microsoft]: azurePlayer,
        [Providers.AzureOpenAi]: azurePlayer,
        [Providers.Afflorithmics]: aflorithmicPlayer,
        [Providers.Elevenlabs]: elevenLabsPlayer,
        [Providers.Google]: googlePlayer,
        [Providers.Amazon]: emptyPlayer,
    };

    return providerToPlayer[voice.provider] ?? emptyPlayer;
};
